import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { WorkloadCostReportItem } from '@cast/types';

dayjs.extend(utc);
dayjs.extend(timezone);
import {
  divideNumericObject,
  generateForecast,
  sumNumericObjects,
} from '@cast/utils';
import { fillGaps } from '@cast/split-me/utils';

import type { ComputeSpendDatapoint } from '../_types';
import type { ComputeGranularity } from './getComputeGranularity';
import { mapWorkloadsToDatapoints } from './mapWorkloadsToDatapoints';

type MapTopMostExpensiveWorkloadDatapointsArgs = {
  range: [Dayjs, Dayjs];
  workloads: WorkloadCostReportItem[];
  timezone: string;
  unit: ComputeGranularity;
};

export const mapTopMostExpensiveWorkloadDatapoints = ({
  range,
  workloads,
  timezone,
  unit,
}: MapTopMostExpensiveWorkloadDatapointsArgs): ComputeSpendDatapoint[] => {
  if (!workloads.length) {
    return [];
  }

  const [from, to] = range;

  const gapsFiller = workloads.reduce(
    (acc, { workloadName }) => ({
      ...acc,
      [workloadName!]: null,
    }),
    {} as Record<string, number | null>
  );

  const datapoints = mapWorkloadsToDatapoints(workloads, unit);

  const presentDate = dayjs().tz(timezone).startOf(unit);
  const applyForecast = to.isAfter(presentDate, unit);
  const firstFutureTimestamp = presentDate.add(1, unit);

  const dataPointsWithFilledGaps = fillGaps({
    timeSeries: datapoints,
    dateRange: { from, to: applyForecast ? presentDate : to },
    filler: (timestamp) => ({
      timestamp: timestamp.toISOString(),
      chartTimestampUnix: new Date(timestamp.toISOString()).getTime() / 1000,
      values: gapsFiller,
    }),
    fillPartial: true,
    unit,
  });

  return applyForecast
    ? generateForecast({
        dateRange: [firstFutureTimestamp, to],
        unit,
        data: dataPointsWithFilledGaps,
        zeroAgg: (timestamp) => ({
          timestamp: timestamp.toISOString(),
          chartTimestampUnix:
            new Date(timestamp.toISOString()).getTime() / 1000,
          values: gapsFiller,
        }),
        sum: (agg, item) => ({
          timestamp: agg.timestamp,
          chartTimestampUnix: agg.chartTimestampUnix,
          values: sumNumericObjects(agg.values, item.values),
        }),
        divide: ({ timestamp, chartTimestampUnix, values }, count) => ({
          timestamp,
          chartTimestampUnix,
          isForecast: true,
          values: divideNumericObject(values, count),
        }),
      })
    : dataPointsWithFilledGaps;
};
